/**
 * ヘルプの見出しの id。画面の「?」から、文書の該当箇所へ飛ぶための錨である。
 *
 * ## 見出しの文字列から決める
 *
 * 文書の側に id を書き足さない。書き足す形にすると、見出しを直したときに
 * id だけが古いまま残り、「?」が黙って先頭を開くようになる。見出しの文字列
 * そのものから決めれば、画面の側の指定と文書の見出しを 1 つの試験で突き合わせ
 * られる（`help.test.tsx` の全走査）。
 *
 * - 空白は `-` にする
 * - `/`・括弧・中黒・句読点などの記号は落とす
 * - 同じ id が 2 つ目以降に出たら `-2`, `-3` を付ける
 */

/** 落とす記号。全角と半角の両方を入れる。 */
const DROPPED =
  /[/／\\()（）[\]［］{}｛｝「」『』【】・、。,.:：;；!！?？"'`“”‘’<>＜＞#＃*＊&＆|｜~〜]/gu;

/** 見出しの文字列から id を作る。空になったら `section` にする。 */
export function headingId(text: string): string {
  const id = text
    .trim()
    .replace(DROPPED, "")
    .replace(/[\s\u3000]+/gu, "-")
    .replace(/-+/gu, "-")
    .replace(/^-|-$/gu, "");
  return id === "" ? "section" : id;
}

/**
 * 並んだ見出しに id を振る。**同じ id の 2 つ目以降に番号を付ける**。
 *
 * 文書の中の順番で振るので、同じ文書からは必ず同じ id の並びが出る。
 */
export function headingIds(texts: readonly string[]): readonly string[] {
  const seen = new Map<string, number>();
  return texts.map((text) => {
    const base = headingId(text);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return count === 1 ? base : `${base}-${String(count)}`;
  });
}

/** 錨を付ける見出しの段。文書の `##` と `###` にあたる。 */
const ANCHORED_TAGS = new Set(["h2", "h3"]);

/** 描画の木の最小の形（hast）。必要なところだけを持つ。 */
interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

function textOf(node: HastNode): string {
  if (node.type === "text") return node.value ?? "";
  return (node.children ?? []).map(textOf).join("");
}

function collectHeadings(node: HastNode, into: HastNode[]): void {
  if (
    node.type === "element" &&
    node.tagName !== undefined &&
    ANCHORED_TAGS.has(node.tagName)
  ) {
    into.push(node);
    return;
  }
  for (const child of node.children ?? []) collectHeadings(child, into);
}

/**
 * `react-markdown` の `rehypePlugins` に渡す。`##` / `###` に id を付ける。
 *
 * 描画の部品（`components`）の中で数えると、描き直しのたびに番号が
 * 進んでしまう（開発時の二重描画で `-2` がずれる）。木を 1 度だけ
 * 書き換える形にしてある。
 */
export function rehypeHeadingIds() {
  return (tree: HastNode): void => {
    const headings: HastNode[] = [];
    collectHeadings(tree, headings);
    const ids = headingIds(headings.map(textOf));
    headings.forEach((heading, index) => {
      heading.properties = { ...heading.properties, id: ids[index] };
    });
  };
}

/**
 * 文書の本文（Markdown）から、`##` / `###` の id を並びどおりに取る。
 *
 * 画面が指す錨が文書に実在するかを、描画せずに確かめるためのもの。
 * 見出しの中の強調や code の記号は落としてから数える（描画後の文字列と同じにする）。
 */
export function headingIdsInMarkdown(body: string): readonly string[] {
  const texts: string[] = [];
  let fenced = false;
  for (const line of body.split(/\r?\n/u)) {
    if (/^\s*(```|~~~)/u.test(line)) {
      fenced = !fenced;
      continue;
    }
    if (fenced) continue;
    const matched = /^(#{1,6})\s+(.*?)\s*#*\s*$/u.exec(line);
    if (matched === null) continue;
    const depth = matched[1]?.length ?? 0;
    const text = (matched[2] ?? "").replace(/[*`]/gu, "");
    if (depth === 2 || depth === 3) texts.push(text);
  }
  return headingIds(texts);
}
